// Iter 82.8 Sprint 2 / Phase E3.0 — Salary Assignments.
// Effective-dated employee × structure mapping. Lists all assignments,
// supports filter by employee + status, allows New assignment + see
// per-employee history timeline.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Plus, MagnifyingGlass, ClockCounterClockwise, TrashSimple, X,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { formatDate } from "../../lib/format";

const STATUS_TONE = {
    Active: "bg-emerald-100 text-emerald-800",
    Superseded: "bg-zinc-100 text-zinc-700",
    Inactive: "bg-slate-100 text-slate-600",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function PayrollAssignments() {
    const { can } = useAuth();
    const canEdit = can("payroll:admin");
    // Iter 111.1 · Obs 10 — accept `?employee_id=EMP-XXX` deep-link so
    // the "Fix" link from the Payroll Run preflight readiness lands on
    // the correct filter + opens the New Assignment dialog pre-populated.
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [structures, setStructures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [employeeFilter, setEmployeeFilter] = useState(searchParams.get("employee_id") || "");
    const [statusFilter, setStatusFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        employee_id: searchParams.get("employee_id") || "",
        structure_id: "", effective_from: todayIso(),
        gross_per_month: "", ctc_per_annum: "", reason: "manual_assignment", remarks: "",
    });
    const [historyEmp, setHistoryEmp] = useState(null);
    const [history, setHistory] = useState(null);
    // If arriving with employee_id, auto-open the create dialog once
    // employee list has loaded (so the operator sees the employee name).
    const [autoOpenDone, setAutoOpenDone] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (employeeFilter) params.employee_id = employeeFilter;
            if (statusFilter) params.status_filter = statusFilter;
            // Primary fetch — assignments list (visible to anyone with payroll:read).
            const list = await api.get("/payroll/assignment/list", { params });
            setItems(list.data.items);
            // Secondary fetches — employees + active structures are only needed for
            // the New Assignment dialog and filter dropdowns. employee:read is gated
            // separately from payroll:read (e.g. accounts has payroll:read but not
            // employee:read) so we fetch them in parallel and silently fall back to
            // empty lists when the caller is not authorised. The assignment table
            // itself already carries snapshot fields and renders fine without them.
            const [empsRes, structsRes] = await Promise.allSettled([
                api.get("/hr/employee/list", { params: { page_size: 200, status_filter: "Active" } }),
                api.get("/payroll/structure/list", { params: { is_active: true } }),
            ]);
            setEmployees(empsRes.status === "fulfilled" ? (empsRes.value.data.items || []) : []);
            setStructures(structsRes.status === "fulfilled" ? (structsRes.value.data.items || []) : []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load assignments"));
        } finally {
            setLoading(false);
        }
    }, [employeeFilter, statusFilter]);

    useEffect(() => { reload(); }, [reload]);

    // Iter 111.1 · Obs 10 — deep-link auto-open of New Assignment dialog.
    // If the deep-linked employee isn't in the paginated Active list
    // (e.g. Probation status, or beyond the 200-item page), fetch them
    // explicitly and inject into the options list so the <select value>
    // finds a matching <option value>.
    useEffect(() => {
        const empIdFromUrl = searchParams.get("employee_id");
        if (!empIdFromUrl || !canEdit || autoOpenDone || employees.length === 0) return;
        // Flip the gate BEFORE any async work so the effect never re-enters
        // on employees[] state changes triggered by the merge below.
        setAutoOpenDone(true);
        const alreadyPresent = employees.some((e) => e.id === empIdFromUrl);
        const proceed = (mergedEmployees) => {
            setEmployees(mergedEmployees);
            setForm((prev) => ({
                ...prev,
                employee_id: empIdFromUrl,
                structure_id: "",
                effective_from: todayIso(),
                gross_per_month: "", ctc_per_annum: "",
                reason: "manual_assignment", remarks: "",
            }));
            setOpen(true);
            // Strip the query param so a subsequent close/reopen does not
            // re-trigger the auto-open.
            const next = new URLSearchParams(searchParams);
            next.delete("employee_id");
            setSearchParams(next, { replace: true });
        };
        if (alreadyPresent) {
            proceed(employees);
        } else {
            api.get(`/hr/employee/${empIdFromUrl}`)
                .then((r) => proceed([r.data, ...employees]))
                .catch(() => {
                    toast.error(`Employee ${empIdFromUrl} not found — please pick from the list.`);
                    proceed(employees);
                });
        }
    }, [searchParams, canEdit, autoOpenDone, employees, setSearchParams]);

    const openHistory = async (emp_id) => {
        try {
            const { data } = await api.get(`/payroll/assignment/history/${emp_id}`);
            setHistory(data);
            setHistoryEmp(emp_id);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load history"));
        }
    };
    const save = async () => {
        try {
            const payload = {
                ...form,
                gross_per_month: form.gross_per_month === "" ? null : Number(form.gross_per_month),
                ctc_per_annum: form.ctc_per_annum === "" ? null : Number(form.ctc_per_annum),
            };
            await api.post("/payroll/assignment", payload);
            toast.success("Assignment created");
            setOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };
    const remove = async (a) => {
        if (!window.confirm(`Delete assignment for ${a.employee_name_snapshot}?`)) return;
        try {
            await api.delete(`/payroll/assignment/${a.id}`);
            toast.success("Deleted");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="payroll-assign-page">
            <PageHeader
                eyebrow="Payroll Masters"
                title="Salary Assignment"
                description={`${items.length} mapping${items.length === 1 ? "" : "s"} · effective-dated employee × structure mappings`}
                actions={(
                    canEdit ? (
                        <Button onClick={() => { setForm({ employee_id: "", structure_id: "", effective_from: todayIso(), gross_per_month: "", ctc_per_annum: "", reason: "manual_assignment", remarks: "" }); setOpen(true); }} size="sm" data-testid="assign-new-btn">
                            <Plus size={14} className="mr-1" /> New Mapping
                        </Button>
                    ) : null
                )}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="w-72">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Employee</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={employeeFilter}
                                onChange={(e) => setEmployeeFilter(e.target.value)} data-testid="assign-emp-filter">
                            <option value="">All employees</option>
                            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_code} — {e.full_name}</option>)}
                        </select>
                    </div>
                    <div className="w-40">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Status</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)} data-testid="assign-status-filter">
                            <option value="">All</option>
                            <option value="Active">Active</option>
                            <option value="Superseded">Superseded</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                    </div>
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="assign-table">
                {loading ? (
                    <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Structure</th>
                                <th className="text-center px-3 py-2 font-medium">v</th>
                                <th className="text-left px-3 py-2 font-medium">Effective</th>
                                <th className="text-right px-3 py-2 font-medium">Gross / Month</th>
                                <th className="text-right px-3 py-2 font-medium">CTC / Year</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Reason</th>
                                <th className="text-left px-3 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((a) => (
                                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`assign-row-${a.id}`}>
                                    <td className="px-3 py-2">
                                        <div className="text-sm font-semibold">{a.employee_name_snapshot}</div>
                                        <div className="text-xs text-slate-500 font-mono">{a.employee_code_snapshot}</div>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">{a.structure_code}</td>
                                    <td className="px-3 py-2 text-center"><Badge className="bg-slate-100 text-slate-700 text-[10px]">v{a.structure_version_no}</Badge></td>
                                    <td className="px-3 py-2 text-xs">{formatDate(a.effective_from)}{a.effective_to ? ` → ${formatDate(a.effective_to)}` : ""}</td>
                                    <td className="px-3 py-2 text-right font-mono">{a.gross_per_month ? inr(a.gross_per_month) : "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono">{a.ctc_per_annum ? inr(a.ctc_per_annum) : "—"}</td>
                                    <td className="px-3 py-2"><Badge className={`text-[10px] ${STATUS_TONE[a.status] || ""}`}>{a.status}</Badge></td>
                                    <td className="px-3 py-2 text-xs">{a.reason}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-1">
                                            <Button variant="ghost" size="sm" onClick={() => openHistory(a.employee_id)} data-testid={`assign-hist-${a.id}`}>
                                                <ClockCounterClockwise size={12} />
                                            </Button>
                                            {!a.is_consumed_by_payroll && canEdit && (
                                                <Button variant="ghost" size="sm" onClick={() => remove(a)} data-testid={`assign-del-${a.id}`}>
                                                    <TrashSimple size={12} className="text-rose-600" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-12 text-sm text-slate-400">No assignments yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl" data-testid="assign-dialog">
                    <DialogHeader>
                        <DialogTitle>New Salary Assignment</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Employee</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2" value={form.employee_id}
                                    onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                                    data-testid="assign-form-emp">
                                <option value="">— Select Employee —</option>
                                {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_code} — {e.full_name}</option>)}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Salary Structure</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2" value={form.structure_id}
                                    onChange={(e) => setForm({ ...form, structure_id: e.target.value })}
                                    data-testid="assign-form-struct">
                                <option value="">— Select Structure —</option>
                                {structures.filter((s) => !s.effective_to).map((s) => (
                                    <option key={s.id} value={s.id}>{s.code} v{s.version_no} — {s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Effective From</Label>
                            <Input type="date" value={form.effective_from}
                                   onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                                   data-testid="assign-form-from" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Reason</Label>
                            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                   placeholder="manual_assignment / annual_increment / promotion"
                                   data-testid="assign-form-reason" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Gross / Month (₹)</Label>
                            <Input type="number" value={form.gross_per_month}
                                   onChange={(e) => setForm({ ...form, gross_per_month: e.target.value })}
                                   data-testid="assign-form-gross" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">CTC / Year (₹) optional</Label>
                            <Input type="number" value={form.ctc_per_annum}
                                   onChange={(e) => setForm({ ...form, ctc_per_annum: e.target.value })}
                                   data-testid="assign-form-ctc" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Remarks</Label>
                            <textarea className="w-full border rounded-md text-sm p-2" rows={2}
                                      value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                                      data-testid="assign-form-remarks" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} data-testid="assign-form-cancel">Cancel</Button>
                        <Button onClick={save} data-testid="assign-form-save">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!historyEmp} onOpenChange={(o) => { if (!o) { setHistoryEmp(null); setHistory(null); } }}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="assign-hist-dialog">
                    <DialogHeader>
                        <DialogTitle>Assignment History · {history?.employee_id}</DialogTitle>
                    </DialogHeader>
                    {history && history.items.length === 0 && (
                        <div className="text-center py-8 text-sm text-slate-400">No assignments.</div>
                    )}
                    {history && history.items.length > 0 && (
                        <ol className="relative border-l-2 border-slate-200 ml-3" data-testid="assign-hist-timeline">
                            {history.items.map((h) => (
                                <li key={h.id} className="mb-4 ml-4" data-testid={`hist-${h.id}`}>
                                    <div className="absolute w-3 h-3 bg-sky-500 rounded-full -left-[7px] mt-1.5" />
                                    <div className="text-sm font-semibold">{h.structure_code} v{h.structure_version_no}</div>
                                    <div className="text-xs text-slate-500">{formatDate(h.effective_from)}{h.effective_to ? ` → ${formatDate(h.effective_to)}` : " (current)"}</div>
                                    <div className="text-xs text-slate-700 mt-1">Gross / Month: <strong>{h.gross_per_month ? inr(h.gross_per_month) : "—"}</strong> · {h.reason}</div>
                                </li>
                            ))}
                        </ol>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
